import { useEffect, useRef, useState } from 'react';
import { ArrowDownAZ, ArrowUpAZ, Search, X } from 'lucide-react';
import type { SiteListState } from '@/lib/site/siteListParams';

// ============================================================================
// components/site/SiteListFilters.tsx — ПАНЕЛЬ ОТБОРОВ БЛОКА `site_list`:
// поиск (LIKE по одному полю), отборы по полям (перечисление → селект
// синонимов, булево → Да/Нет, прочее → точное значение), сортировка
// (поле + направление). Всё состояние — в query-строке (SSoT — siteListParams),
// данные фильтрует СЕРВЕР. Оформление — токенами сайта (--site-*).
// ============================================================================

export interface SiteListFieldMeta {
  column: string;
  name: string;
  synonym: string;
  dataType: string;
  format: string;
  refTable: string;
  isRef: boolean;
}

export interface SiteListFilterBarProps {
  fields: SiteListFieldMeta[];
  filterFields: string[];
  searchField: string;
  searchPlaceholder?: string;
  sortFields: string[];
  /** Ширина панели — как у блока (appearance.maxWidth), иначе --site-max-width. */
  maxWidth?: string;
  /** Значения перечислений: код перечисления → [{value, label}]. */
  enumOptions: Record<string, Array<{ value: string; label: string }>>;
  state: SiteListState;
  onSearch: (q: string) => void;
  onField: (field: string, value: string) => void;
  onSort: (sort: string, sortDesc: boolean) => void;
  onReset: () => void;
}

const control: React.CSSProperties = {
  height: 34,
  borderRadius: 8,
  border: '1px solid var(--site-border-color)',
  background: 'color-mix(in oklab, var(--site-surface) 85%, transparent)',
  color: 'var(--site-text)',
  padding: '0 10px',
  fontSize: 14,
  minWidth: 0,
};

/** SearchBox — поиск по строке: применяется по Enter/паузе ввода (400 мс). */
function SearchBox({ value, placeholder, onCommit }: { value: string; placeholder: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = (v: string) => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (v !== value) onCommit(v);
  };
  const change = (v: string) => {
    setDraft(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; onCommit(v); }, 400);
  };
  return (
    <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180 }}>
      <Search style={{ position: 'absolute', left: 10, top: 9, width: 16, height: 16, color: 'var(--site-muted)' }} />
      <input
        type="search"
        value={draft}
        placeholder={placeholder}
        style={{ ...control, width: '100%', paddingLeft: 32 }}
        onChange={(e) => change(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }}
      />
    </div>
  );
}

/** FieldControl — отбор по одному полю (селект/точное значение). */
function FieldControl({
  field, value, enumValues, onCommit,
}: {
  field: SiteListFieldMeta;
  value: string;
  enumValues: Array<{ value: string; label: string }>;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const label = field.synonym || field.name || field.column;
  const dt = field.dataType.toLowerCase();

  if (dt === 'enumeration' && enumValues.length > 0) {
    return (
      <select
        aria-label={label}
        title={label}
        style={control}
        value={value}
        onChange={(e) => onCommit(e.target.value)}
      >
        <option value="">{label}: все</option>
        {enumValues.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (dt === 'boolean' || dt === 'bool') {
    return (
      <select aria-label={label} title={label} style={control} value={value} onChange={(e) => onCommit(e.target.value)}>
        <option value="">{label}: все</option>
        <option value="true">Да</option>
        <option value="false">Нет</option>
      </select>
    );
  }
  return (
    <input
      type="text"
      aria-label={label}
      title={`${label} (точное значение)`}
      placeholder={`${label}…`}
      style={{ ...control, width: 170 }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft !== value) onCommit(draft); }}
      onKeyDown={(e) => { if (e.key === 'Enter' && draft !== value) onCommit(draft); }}
    />
  );
}

export function SiteListFilters({
  fields, filterFields, searchField, searchPlaceholder, sortFields, maxWidth, enumOptions, state,
  onSearch, onField, onSort, onReset,
}: SiteListFilterBarProps) {
  const byColumn = new Map(fields.map((f) => [f.column, f]));
  const activeCount =
    (state.q ? 1 : 0) +
    (state.sort ? 1 : 0) +
    Object.values(state.fieldValues).filter((v) => v !== '').length;
  const hasBar = !!searchField || filterFields.length > 0 || sortFields.length > 0;
  if (!hasBar) return null;

  const sortAsc = !state.sortDesc;
  const sortActive = state.sort !== '';

  return (
    <div
      style={{
        maxWidth: maxWidth || 'var(--site-max-width)',
        margin: '0 auto 14px',
        padding: '0 24px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {searchField && (
        <SearchBox
          value={state.q}
          placeholder={searchPlaceholder || 'Поиск…'}
          onCommit={onSearch}
        />
      )}
      {filterFields.map((col) => {
        const f = byColumn.get(col);
        if (!f) return null;
        return (
          <FieldControl
            key={col}
            field={f}
            value={state.fieldValues[col] || ''}
            enumValues={enumOptions[f.refTable] || []}
            onCommit={(v) => onField(col, v)}
          />
        );
      })}
      {sortFields.length > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <select
            aria-label="Сортировка"
            title="Сортировка"
            style={control}
            value={state.sort}
            onChange={(e) => onSort(e.target.value, state.sortDesc)}
          >
            <option value="">Порядок: по умолчанию</option>
            {sortFields.map((col) => {
              const f = byColumn.get(col);
              return <option key={col} value={col}>{f ? (f.synonym || f.name) : col}</option>;
            })}
          </select>
          <button
            type="button"
            title={sortAsc ? 'По возрастанию' : 'По убыванию'}
            disabled={!sortActive}
            style={{ ...control, width: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: sortActive ? 1 : 0.4 }}
            onClick={() => onSort(state.sort, !state.sortDesc)}
          >
            {sortAsc ? <ArrowUpAZ style={{ width: 16, height: 16 }} /> : <ArrowDownAZ style={{ width: 16, height: 16 }} />}
          </button>
        </span>
      )}
      {activeCount > 0 && (
        <button
          type="button"
          onClick={onReset}
          style={{ ...control, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
        >
          <X style={{ width: 14, height: 14 }} /> Сбросить ({activeCount})
        </button>
      )}
    </div>
  );
}
